import {
  CheckInOrderedProjectRequest,
  DeleteOrderedProjectRequest,
  FinishOrderedProjectRequest,
  GetOrderedProjectsRequest,
  Order,
  OrderedProject,
  OrderedProjectDurationSummary,
  OrderRequest,
  SendAdditionalDoneProjectsRequest,
  SummedProjectOrders,
  UpdateOrderedProjectRequest,
  ViewState,
  WeeklyThroughput,
  DoneTotals,
} from '@its4plan/ordering';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { Project } from '~/common/entities/Project/ProjectTypes';
import { useApiContext } from '~/common/api/common/apiCommons';

type MutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, Error, TVariables>,
  'mutationFn'
>;

export type AggregatedOrderedProject = Omit<
  OrderedProject,
  'project' | 'options'
> & {
  project: Project;
  options: AggregatedOrderedProject[];
};

export type AggregatedSummedProjectOrders = Omit<
  SummedProjectOrders,
  'project' | 'orderedProjects'
> & {
  project: Project;
  orderedProjects: AggregatedOrderedProject[];
};

export function useSummedOrderedProjects<T = AggregatedSummedProjectOrders[]>(
  options?: UseQueryOptions<AggregatedSummedProjectOrders[], Error, T> & {
    viewState?: ViewState;
  },
) {
  const { summedOrderedProjectApi: summedOrderedProjectApi } = useApiContext();
  return useQuery({
    queryKey: ['summed', 'orderedProject', options?.viewState],
    queryFn: async () => {
      const projectsById = await projectById();
      const summedOrderProjects =
        await summedOrderedProjectApi.getSummedOrderedProjects({
          viewState: options?.viewState,
        });
      return mapSummedOrderedProject(summedOrderProjects, projectsById);
    },
    refetchInterval: 5000,
    ...options,
  });
}

export function useGetProjectsWithAmounts(
  startDate: Date,
  endDate: Date,
  options?: UseQueryOptions<OrderedProjectDurationSummary[], Error>,
) {
  const { orderedProjectApi } = useApiContext();
  return useQuery({
    queryKey: ['doneOrdersHistory', startDate, endDate],
    queryFn: async () =>
      await orderedProjectApi.getOrderedProjectsDurationSummary({
        startDate,
        endDate,
      }),
    ...options,
  });
}

export function useSendAdditionalDoneProjects(
  options?: UseMutationOptions<void, Error, SendAdditionalDoneProjectsRequest>,
) {
  const { orderedProjectApi } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: SendAdditionalDoneProjectsRequest) =>
      orderedProjectApi.sendAdditionalDoneProjects({
        sendAdditionalDoneProjectsRequest: req,
      }),
    ...options,
    onSuccess: async (...args) => {
      await options?.onSuccess?.(...args);
      await queryClient.invalidateQueries({ queryKey: ['doneOrdersHistory'] });
    },
  });
}

export function useOrderedProjects<T = OrderedProject[]>(
  options?: UseQueryOptions<OrderedProject[], Error, T> & {
    request?: GetOrderedProjectsRequest;
  },
) {
  const { orderedProjectApi } = useApiContext();
  return useQuery({
    queryKey: ['orderedProject', options?.request],
    queryFn: () => orderedProjectApi.getOrderedProjects(options?.request),
    refetchInterval: 5000,
    ...options,
  });
}

export function useDeleteOrderedProject(
  options?: MutationOptions<void, DeleteOrderedProjectRequest>,
) {
  const { orderedProjectApi } = useApiContext();
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request) => orderedProjectApi.deleteOrderedProject(request),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries();
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdateOrderedProject(
  options?: MutationOptions<void, UpdateOrderedProjectRequest>,
) {
  const { orderedProjectApi } = useApiContext();
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request) => orderedProjectApi.updateOrderedProject(request),
    onSuccess: async (...args) => {
      options?.onSuccess?.(...args);
      await queryClient.invalidateQueries();
    },
  });
}

export function useFinishOrderedProject(
  options?: MutationOptions<OrderedProject, FinishOrderedProjectRequest>,
) {
  const { orderedProjectApi } = useApiContext();
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request) => orderedProjectApi.finishOrderedProject(request),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries();
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useCheckInOrderedProject(
  options?: MutationOptions<OrderedProject, CheckInOrderedProjectRequest>,
) {
  const { orderedProjectApi } = useApiContext();
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request) => orderedProjectApi.checkInOrderedProject(request),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      await queryClient.invalidateQueries();
    },
  });
}

export function useCreateOrder(options?: MutationOptions<Order, OrderRequest>) {
  const { orderApi } = useApiContext();
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (orderRequest) => orderApi.order({ orderRequest }),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      await queryClient.invalidateQueries();
    },
  });
}

export function useCreateOrders(
  options?: MutationOptions<Order[], OrderRequest[]>,
) {
  const { orderApi } = useApiContext();
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (orders) =>
      Promise.all(
        orders.map((orderRequest) => orderApi.order({ orderRequest })),
      ),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      await queryClient.invalidateQueries();
    },
  });
}

export function useWeeklyThroughput(
  start: string,
  end: string,
  options?: UseQueryOptions<WeeklyThroughput[], Error>,
) {
  const { orderedProjectHistoryApi } = useApiContext();
  return useQuery({
    queryKey: ['orderedProjectHistory', 'weeklyThroughput', start, end],
    queryFn: () =>
      orderedProjectHistoryApi.getWeeklyThroughput({
        start: new Date(start),
        end: new Date(end),
      }),
    ...options,
  });
}

export function useDoneTotals(
  start: string,
  end: string,
  projectId: number | null,
  options?: UseQueryOptions<DoneTotals, Error>,
) {
  const { orderedProjectHistoryApi } = useApiContext();
  return useQuery({
    queryKey: ['orderedProjectHistory', 'doneTotals', start, end, projectId],
    queryFn: () =>
      orderedProjectHistoryApi.getDoneTotals({
        start: new Date(start),
        end: new Date(end),
        projectId: projectId!,
      }),
    enabled: projectId != null,
    ...options,
  });
}

async function projectById() {
  const projects = await ProjectApi.queries.getProjects();
  return projects.reduce(
    (acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as { [key: number]: Project },
  );
}

function mapSummedOrderedProject(
  summedOrderProjects: SummedProjectOrders[],
  projectsById: { [key: string]: Project },
): AggregatedSummedProjectOrders[] {
  return summedOrderProjects.map((sop) => {
    return {
      ...sop,
      project: projectsById[sop.project.foreignId]!,
      orderedProjects: sop.orderedProjects.map((op) =>
        mapOrderedProject(op, projectsById),
      ),
    };
  });
}

function mapOrderedProject(
  orderedProject: OrderedProject,
  projectById: { [key: number]: Project },
): AggregatedOrderedProject {
  return {
    ...orderedProject,
    project: projectById[orderedProject.project.foreignId]!,
    options: orderedProject.options.map((op) =>
      mapOrderedProject(op, projectById),
    ),
  };
}
